import { request } from "../../api";
import { headers } from "../../../../config/config";
import { MESSAGE } from "../../../../constants/api/message";
import { AUTHORIZATION } from "../../../../constants/api/auth";
import { StatusCodes } from "http-status-codes";
import { HealthCardAsset } from "../../../../@types/healthCardConfig.types";

const { post, get, del } = request;
const { Authorization, Bearer } = AUTHORIZATION;

// GET/POST/DELETE /api/v1/member-portal-UI/health-card-asset — groupOwnerAuth
// gated, same auth pattern as the sibling health-card-config routes.
const initialRoute = "member-portal-UI/health-card-asset";

const authHeaders = () => ({
	...headers,
	[Authorization]: `${Bearer} ${localStorage.getItem("@jwt")}`
});

// Same shape as healthCardConfig.ts's error handling: a 400/403 means the
// backend already produced a user-facing message (duplicate asset_key,
// cross-tenant guard, missing file) — surface it and stop; anything else is
// unexpected and should propagate.
const handleKnownErrors = (error: any) => {
	if (error?.response?.status === StatusCodes.BAD_REQUEST || error?.response?.status === StatusCodes.FORBIDDEN) {
		const { message, errors } = error.response.data ?? {};
		alert(errors?.length ? `${message}: ${errors.join(", ")}` : message);
		return;
	}
	throw error;
};

// group_number omitted -> global assets only (Super Admin library view);
// given -> global ∪ that group's own assets.
export const getHealthCardAssets = async (group_number?: number) => {
	try {
		const response = await get(initialRoute, authHeaders(), group_number ? { group_number } : {});
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.get.succ) {
				const {
					data: { result }
				} = response;
				return result as HealthCardAsset[];
			}
		}
		return [];
	} catch (error: any) {
		handleKnownErrors(error);
		return [];
	}
};

// formData carries the image under "attachment" plus a JSON-stringified
// {asset_key, label, group_number?} field under "health_card_asset" — mirrors
// groupBranding.ts's upload shape, which the backend multer config is built
// the same way for.
export const uploadHealthCardAsset = async (formData: FormData) => {
	try {
		const response = await post(initialRoute, formData, authHeaders());
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.post.succ) {
				const {
					data: { result }
				} = response;
				return result;
			}
			throw new Error();
		}
		throw new Error();
	} catch (err) {
		handleKnownErrors(err);
		throw err;
	}
};

export const deleteHealthCardAsset = async (id: string) => {
	try {
		const endpoint = `${initialRoute}/${id}`;
		const response = await del(endpoint, authHeaders());
		if (response) {
			const {
				data: { message }
			} = response;
			if (message === MESSAGE.delete.succ) {
				return true;
			}
			throw new Error();
		}
		throw new Error();
	} catch (err) {
		handleKnownErrors(err);
		throw err;
	}
};

export const healthCardAsset = {
	getHealthCardAssets,
	uploadHealthCardAsset,
	deleteHealthCardAsset
};
